"use client";

import React, { useState } from 'react';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { usePathname } from 'next/navigation';
import DrawerList from './DrawerList';
import { navItems, DRAWER_WIDTH } from './NavItems';

export default function SiteDrawer() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = useState(isMdUp);
  const [drawerTop, setDrawerTop] = useState<number>(0);
  const lastModeRef = React.useRef<'mobile' | 'desktop'>('mobile');

  const toggle = () => setOpen((s) => !s);

  React.useEffect(() => {
    let rafId: number | null = null;

    const doUpdate = () => {
      rafId = null;
      const header = document.querySelector('.MuiAppBar-root') as HTMLElement | null || document.querySelector('header') as HTMLElement | null;
      const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      setDrawerTop((prev) => {
        const next = Math.round(top);
        return prev === next ? prev : next;
      });
    };

    const scheduleUpdate = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(doUpdate);
    };

    // initial
    scheduleUpdate();

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    const toggleListener = () => {
      setOpen((s) => !s);
    };
    window.addEventListener('site-drawer-toggle', toggleListener as EventListener);

    return () => {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('site-drawer-toggle', toggleListener as EventListener);
    };
  }, [isMdUp]);

  React.useEffect(() => {
    if (isMdUp && lastModeRef.current !== 'desktop') {
      setOpen(true);
      lastModeRef.current = 'desktop';
    }
    if (!isMdUp && lastModeRef.current !== 'mobile') {
      setOpen(false);
      lastModeRef.current = 'mobile';
    }
  }, [isMdUp]);

  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    const offset = isMdUp && open ? 'var(--drawer-width)' : '0px';
    document.documentElement.style.setProperty('--drawer-offset', offset);
  }, [isMdUp, open]);

  const pathname = usePathname();

  return (
    <>
      <Drawer
        anchor="left"
        open={isMdUp ? open : open}
        variant={isMdUp ? 'persistent' : 'temporary'}
        onClose={toggle}
        ModalProps={isMdUp ? undefined : { keepMounted: true }}
        PaperProps={{ sx: { width: DRAWER_WIDTH, position: 'fixed', top: `${drawerTop}px`, left: 0, height: `calc(100vh - ${drawerTop}px)`, boxSizing: 'border-box', zIndex: 1200, borderRight: '1px solid rgba(0,0,0,0.08)', bgcolor: 'background.paper' } }}
      >
        <DrawerList items={navItems} pathname={pathname} onItemClick={() => { if (!isMdUp) setOpen(false); }} />
      </Drawer>
    </>
  );
}
